import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { createRoot } from 'react-dom/client';
import { default as initEngineModule, start as startGame, start_sandbox as startSandbox, Game, set_panic_hook, set_tracing_hook, Status } from '../out/client_web';
import './app.css';
import MobileControls from './MobileControls';
import { startEditorBridge } from './editorBridge';
import { LogLevel, recordSandboxLog, startConsoleCapture } from './consoleCapture';

// Before anything else runs, so a client that fails on the way up says so in
// the editor's log rather than only in a browser console nobody can see.
startConsoleCapture();

declare global {
    function init_sandbox(assets_url: string): MessagePort;

    var sandbox_iframe: HTMLIFrameElement | null;
}


// The subdomain names the game: voidrunner.aetherlight.io, or
// voidrunner.localhost locally. A bare domain leaves it to nexus's default.
function gameFromHost(hostname: string): string | null {
    const labels = hostname.split('.');
    const last = labels[labels.length - 1];
    if (/^\d+$/.test(last) || labels[0] === 'www') {
        return null;
    }
    return labels.length >= (last === 'localhost' ? 2 : 3) ? labels[0] : null;
}

globalThis.init_sandbox = (assets_url: string) => {
    let channel = new MessageChannel();

    // if (globalThis.sandbox_iframe != null) {
    //     document.head.removeChild(globalThis.sandbox_iframe);
    //     globalThis.sandbox_iframe = null;
    // }

    let log_channel = new MessageChannel();
    // The sandbox is a document of its own with no way to reach the editor
    // host, so what it says is relayed into a log of its own from here. A panic
    // is the one kind that carries its text in `error` rather than `message`.
    const sandboxLevels: { [type: string]: LogLevel } = {
        panic: "error",
        error: "error",
        warn: "warn",
        info: "info",
        log: "log",
    };
    log_channel.port1.onmessage = (event) => {
        const level = sandboxLevels[event.data?.type];
        if (level === undefined) {
            console.warn("Unknown message type:", event.data);
            return;
        }
        const text = event.data.type == "panic"
            ? "panic: " + event.data.error
            : event.data.message;
        recordSandboxLog(level, text);
    };

    let iframe = document.createElement("iframe");
    globalThis.sandbox_iframe = iframe;
    iframe.width = "0";
    iframe.height = "0";
    iframe.sandbox = "allow-scripts";
    iframe.src = `${window.location.protocol}//${window.location.host}/sandbox.html`;
    document.head.appendChild(iframe);
    window.addEventListener("message", (event) => {
        // This window is spoken to by more than the sandbox — an editor host
        // sends its requests here too — so anything from elsewhere is left to
        // whoever it was meant for rather than complained about.
        if (event.source !== iframe.contentWindow) {
            return;
        }

        if (event.data == "loadModule") {
            if (iframe.contentWindow == null) {
                console.error("iframe contentWindow is null");
            }
            else {
                iframe.contentWindow?.postMessage({ type: "init", assets_url: assets_url }, "*", [channel.port2, log_channel.port2]);
            }
        } else {
            console.warn("Unknown message type:", event.data);
        }
    });

    return channel.port1;
}


// Loading state types
type LoadingPhase =
    | { stage: 'requesting'; message: string }
    | { stage: 'downloading'; loaded: number; total: number | null; message: string }
    | { stage: 'initializing'; message: string }
    | { stage: 'connecting'; message: string; raw?: any }
    | { stage: 'ready'; message: string }
    | { stage: 'error'; message: string; error?: any };

const initialLoading: LoadingPhase = { stage: 'requesting', message: 'Requesting game resources…' };

const LoadingScreen = ({ phase }: { phase: LoadingPhase }) => {
    let percent: number | null = null;
    if (phase.stage === 'downloading') {
        if (phase.total && phase.total > 0) {
            percent = Math.min(100, Math.round((phase.loaded / phase.total) * 100));
        } else if (phase.loaded > 0) {
            // Indeterminate total size: show a capped pseudo-progress
            percent = Math.min(95, 5 + Math.round(Math.log10(phase.loaded + 1) * 15));
        }
    } else if (phase.stage === 'initializing') {
        percent = 95; // near completion
    } else if (phase.stage === 'ready') {
        percent = 100;
    }

    return (
        <div style={styles.overlay}>
            <div style={styles.panel}>
                <h1 style={styles.title}>Aether</h1>
                <div style={styles.status}>{phase.message}</div>
                {percent !== null && (
                    <div style={styles.progressOuter}>
                        <div style={{ ...styles.progressInner, width: `${percent}%` }} />
                    </div>
                )}
                {phase.stage === 'error' && (
                    <div style={styles.error}>An error occurred starting the game. Check console logs.</div>
                )}
            </div>
        </div>
    );
};

const styles: { [k: string]: any } = {
    overlay: {
        position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'radial-gradient(circle at 30% 30%, #1d2530 0%, #0c1116 70%)', color: '#e2e8f0',
        fontFamily: 'system-ui, sans-serif', zIndex: 1000
    },
    panel: { width: 'min(460px, 90%)', padding: '2.25rem 2rem 2.5rem', background: 'rgba(15,23,32,0.72)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, boxShadow: '0 4px 32px -4px rgba(0,0,0,0.6)' },
    title: { margin: '0 0 1.25rem', fontSize: '1.9rem', letterSpacing: '0.06em', fontWeight: 600, background: 'linear-gradient(90deg,#6ee7b7,#3b82f6)', WebkitBackgroundClip: 'text', color: 'transparent' },
    status: { marginBottom: '1rem', fontSize: '0.95rem', opacity: 0.9, lineHeight: 1.4 },
    progressOuter: { position: 'relative', width: '100%', height: 10, background: 'rgba(255,255,255,0.08)', borderRadius: 6, overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)' },
    progressInner: { height: '100%', background: 'linear-gradient(90deg,#10b981,#0ea5e9)', transition: 'width 180ms ease-out' },
    error: { marginTop: '1rem', fontSize: '0.8rem', color: '#f87171' }
};

const App = () => {
    const { isAuthenticated, accessToken, isLoading, isUnauthorized, startDiscordLogin, doGuestLogin } = useAuth();
    const [gameModuleLoaded, setGameModuleLoaded] = useState<boolean>(false);
    const [game, setGame] = useState<Game | null>(null as any);
    const [connectShard, setConnectShard] = useState<string | null>(null);
    const [loading, setLoading] = useState(initialLoading as LoadingPhase);

    // Mobile control handlers
    const handleLeftStickMove = (x: number, y: number) => {
        game?.left_stick(x, -y);
    };

    const handleRightStickMove = (deltaX: number, deltaY: number) => {
        game?.mouse_move(0., 0., deltaX * 4., deltaY * 4., true);
    };

    const handleJumpPress = () => {
        game?.key_down("Space");
        game?.key_up("Space");
    };

    const handleSprintStart = () => {
        game?.key_down("ShiftLeft");
    };

    const handleSprintEnd = () => {
        game?.key_up("ShiftLeft");
    };

    // Set shard name from hash any time the hash changes
    useEffect(() => {
        const updateShardFromHash = () => {
            const hash = window.location.hash;
            if (hash.startsWith("#")) {
                setConnectShard(hash.substring(1));
            } else {
                setConnectShard(null);
            }
        };
        updateShardFromHash();
        window.addEventListener("hashchange", updateShardFromHash);
        return () => {
            window.removeEventListener("hashchange", updateShardFromHash);
        };
    }, []);

    const loadGame = useCallback(async () => {
        try {
            setLoading({ stage: 'requesting', message: 'Requesting WASM module…' });

            // Use XHR for progress because fetch streaming progress for wasm isn't widely reliable across all servers
            const wasmBytes = await new Promise<ArrayBuffer>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('GET', '/client_web_bg.wasm');
                xhr.responseType = 'arraybuffer';
                xhr.onprogress = (e) => {
                    setLoading({ stage: 'downloading', loaded: e.loaded, total: e.lengthComputable ? e.total : null, message: 'Downloading engine…' });
                };
                xhr.onerror = () => reject(new Error('Network error fetching WASM'));
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(xhr.response);
                    } else {
                        reject(new Error(`Failed to load WASM: ${xhr.status} ${xhr.statusText}`));
                    }
                };
                xhr.send();
            });

            setLoading({ stage: 'initializing', message: 'Initializing engine…' });
            await initEngineModule(new Uint8Array(wasmBytes));
            setGameModuleLoaded(true);
        } catch (err) {
            console.error('Failed to start game:', err);
            setLoading({ stage: 'error', message: 'Failed to start game', error: err });
        }
    }, []);


    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            doGuestLogin();
        } else {
            console.info("Logged in with access token, proceeding to load game");
        }
    }, [isAuthenticated, isLoading]);

    useEffect(() => {
        if (gameModuleLoaded && accessToken) {
            let canvas = document.getElementById('canvas') as HTMLCanvasElement | null;
            if (canvas != null) {
                const width = Math.round(document.body.clientWidth * window.devicePixelRatio);
                const height = Math.round(document.body.clientHeight * window.devicePixelRatio);
                canvas.width = width;
                canvas.height = height;

                set_panic_hook((error: any) => {
                    console.error("Panic:", error);
                });
                set_tracing_hook((message: any) => {
                    console.log(message);
                });

                let nexus_url = `${window.location.protocol}//${window.location.host}/api/`;
                let game = startGame(nexus_url, canvas, accessToken, connectShard, gameFromHost(window.location.hostname));

                setGame(game as Game);
                // Start in connecting state; we'll promote to ready when status reports InWorld
                setLoading({ stage: 'connecting', message: 'Connecting to servers…' });
            } else {
                throw new Error('Canvas not found');
            }
        }
    }, [gameModuleLoaded, accessToken, connectShard]);

    useEffect(() => {
        loadGame();

        // Prevent iOS safari behaviors
        const preventDefault = (e: Event) => {
            e.preventDefault();
        };

        // Prevent zoom, scroll, and other gestures
        document.addEventListener('touchstart', preventDefault, { passive: false });
        document.addEventListener('touchmove', preventDefault, { passive: false });
        document.addEventListener('touchend', preventDefault, { passive: false });
        document.addEventListener('gesturestart', preventDefault, { passive: false });
        document.addEventListener('gesturechange', preventDefault, { passive: false });
        document.addEventListener('gestureend', preventDefault, { passive: false });

        return () => {
            document.removeEventListener('touchstart', preventDefault);
            document.removeEventListener('touchmove', preventDefault);
            document.removeEventListener('touchend', preventDefault);
            document.removeEventListener('gesturestart', preventDefault);
            document.removeEventListener('gesturechange', preventDefault);
            document.removeEventListener('gestureend', preventDefault);
        };
    }, []);

    useEffect(() => {
        if (game) {
            console.log("Game is loaded");

            game?.set_scale_factor(window.devicePixelRatio);

            // Always tick once before rendering
            game?.tick(0.)

            // `?bench` hands the game and each frame's timings to scripts/client-bench.
            const bench = new URLSearchParams(window.location.search).has('bench')
                ? { game, frames: [] as number[] }
                : null;
            (globalThis as any).__aetherBench = bench;

            // On web we do render first and then tick, so the script sandbox can respond
            // by the next frame. Meaning we always render one frame behind, but otherwise
            // input latency would be 2 frames. [citation needed, comment added after the fact]
            var frame_handle: number;
            const tick = (time: number) => {
                const start = bench && performance.now();
                game?.render(time / 1000.);
                const rendered = bench && performance.now();
                if (game) {
                    frame_handle = window.requestAnimationFrame(tick);
                }
                game?.tick(time / 1000.);
                if (bench) {
                    bench.frames.push(time, rendered - start, performance.now() - rendered);
                }
                // After ticking simulation, update connection/loading status using latest status snapshot
                let status = game?.status();
                updateConnectionStatus(status, game?.shard_name());
            };
            frame_handle = window.requestAnimationFrame(tick);

            // Mouse lock and input
            let canvas = document.getElementById('canvas') as HTMLCanvasElement | null;
            const on_mouse_move = (event: MouseEvent) => {
                game?.mouse_move(
                    event.offsetX * window.devicePixelRatio,
                    event.offsetY * window.devicePixelRatio,
                    event.movementX,
                    event.movementY,
                    false
                );
            };
            const on_key_down = (event: KeyboardEvent) => {
                game?.key_down(event.code);
                event.preventDefault();
            };
            const on_key_up = (event: KeyboardEvent) => {
                game?.key_up(event.code);
                event.preventDefault();
            };
            const on_mouse_down = (event: MouseEvent) => {
                // preventDefault below suppresses click-to-focus, which breaks
                // window keydown listeners when the client runs in an iframe
                // (e.g. the VSCode editor panel) — grab keyboard focus explicitly.
                window.focus();
                let button = event.button + 1; // Convert to 1=left, 2=right, 3=middle

                if (game?.mouse_down(button)) {
                    canvas?.requestPointerLock({
                        unadjustedMovement: true,
                    }).then(() => {
                        game?.set_cursor_lock(true);
                    });
                }
                event.preventDefault();
            };
            const on_mouse_up = (event: MouseEvent) => {
                let button = event.button + 1; // Convert to 1=left, 2=right, 3=middle
                game?.mouse_up(button);
                event.preventDefault();
            };

            // Lets an editor host (the VSCode extension) drive this viewport.
            // Inert until the host enables editor mode.
            let stopEditorBridge: (() => void) | undefined;

            if (canvas != null) {
                stopEditorBridge = startEditorBridge(game, canvas);

                canvas.addEventListener("mousemove", on_mouse_move);
                canvas.addEventListener("mousedown", on_mouse_down);
                canvas.addEventListener("mouseup", on_mouse_up);
                window.addEventListener("keydown", on_key_down);
                window.addEventListener("keyup", on_key_up);

                new ResizeObserver((entries) => {
                    // Use canvas dimensions to get physical pixels
                    const width = Math.round(document.body.clientWidth * window.devicePixelRatio);
                    const height = Math.round(document.body.clientHeight * window.devicePixelRatio);
                    console.log(document.body.clientWidth, document.body.clientHeight, window.devicePixelRatio);

                    canvas.width = width;
                    canvas.height = height;

                    game?.resize(width, height);
                }).observe(document.body);

                document.addEventListener("pointerlockchange", () => {
                    game?.set_cursor_lock(document.pointerLockElement === canvas);
                });
            }

            return () => {
                stopEditorBridge?.();
                window.cancelAnimationFrame(frame_handle);
                window.removeEventListener("keydown", on_key_down);
                window.removeEventListener("keyup", on_key_up);
                if (canvas != null) {
                    canvas.removeEventListener("mousemove", on_mouse_move);
                    canvas.removeEventListener("mousedown", on_mouse_down);
                    canvas.removeEventListener("mouseup", on_mouse_up);
                }
            };
        }
    }, [game]);

    // Integrate status checks inside the render/tick loop instead of polling separately
    const updateConnectionStatus = useCallback((status: Status, shard_name: string) => {
        var next;
        switch (status) {
            case Status.Loading:
                next = { stage: 'connecting', message: 'Loading…' } as any; break;
            case Status.LoggingIn:
                next = { stage: 'connecting', message: 'Logging in…' } as any; break;
            case Status.ConnectingNexus:
                next = { stage: 'connecting', message: 'Connecting to nexus…' } as any; break;
            case Status.ConnectingShard:
                next = { stage: 'connecting', message: `Connecting to ${shard_name}…` } as any; break;
            case Status.LoadingRenderer:
                next = { stage: 'initializing', message: `Connected to ${shard_name} (loading renderer assets)` }; break;
            case Status.InitRenderer:
                next = { stage: 'ready', message: `Connected to ${shard_name} (initializing renderer)` }; break;
            case Status.Connected:
                next = { stage: 'ready', message: `Connected to ${shard_name}` }; break;
            case Status.Disconnected:
                next = { stage: 'error', message: 'Disconnected' }; break;
        }
        if (next) {
            setLoading((prev: LoadingPhase) => prev.stage === next!.stage && prev.message === next!.message ? prev : next!);
        }
    }, [loading.stage, game]);

    return (
        <>
            <canvas id="canvas" style={{ display: 'block', visibility: game && loading.stage === 'ready' ? 'visible' : 'hidden' }}></canvas>
            {loading.stage !== 'ready' && <LoadingScreen phase={loading} />}
            {game && (
                <MobileControls
                    onLeftStickMove={handleLeftStickMove}
                    onRightStickMove={handleRightStickMove}
                    onJumpPress={handleJumpPress}
                    onSprintStart={handleSprintStart}
                    onSprintEnd={handleSprintEnd}
                />
            )}
        </>
    );
};

document.addEventListener("DOMContentLoaded", function () {
    const rootNode = document.getElementById('root') as HTMLElement;
    const root = createRoot(rootNode);

    root.render(
        <AuthProvider>
            <App />
        </AuthProvider>
    );
});

async function fetch_string(url: string): Promise<string> {
    let response = await fetch(url);
    return await response.text();
}
